import React, { createContext, useContext, useEffect, useState } from 'react'
import { useGetProfileQuery } from '@api/user'
import Loading from '@components/Loading'
import { ApiError } from '@src/types/api'
import { Navigate, useLocation, useSearchParams } from 'react-router'
import { useAppDispatch } from '@src/hooks'
import { setExistsNotifications, setProfile } from '@store/app'
import { useLocalStorage } from 'usehooks-ts'
import ErrorPage from '@components/ErrorPage'
import { statisticApi } from '@api/statistic'
import { dealersApi } from '@api/dealers'
import { leadsApi } from '@api/leads'
import { adminApi } from '@api/admin'
import { notificationsApi } from '@api/notifications'

type AuthContextProps = {
  signIn: (accessToken: string) => Promise<void>;
  signOut: () => Promise<void>;
  getToken: () => string | null;
}

const AuthContext = createContext<AuthContextProps | undefined>(undefined)

type AuthProviderProps = {
  children: React.ReactNode;
}

const AuthProvider = ({ children }: AuthProviderProps) => {
  const [ token, setToken, removeToken ] = useLocalStorage<string | null>('access_token', null)
  const { data: profile, isFetching, error, refetch } = useGetProfileQuery(undefined, {
    refetchOnMountOrArgChange: true,
    refetchOnFocus: true,
    pollingInterval: token ? 5000 : undefined,
  })
  const dispatch = useAppDispatch()
  const [ loading, setLoading ] = useState<boolean>(false)
  const [ searchParams ] = useSearchParams()
  const { pathname } = useLocation()

  const signIn = async (accessToken: string) => {
    setLoading(true)
    setToken(accessToken)
    await refetch()
    setLoading(false)
  }

  const signOut = async () => {
    setLoading(true)
    removeToken()
    await refetch()
    setLoading(false)

    dispatch(notificationsApi.util.resetApiState())
    dispatch(statisticApi.util.resetApiState())
    dispatch(dealersApi.util.resetApiState())
    dispatch(leadsApi.util.resetApiState())
    dispatch(adminApi.util.resetApiState())
  }

  useEffect(() => {
    const storageEventHandler = async (e: StorageEvent) => {
      if (e.key === 'access_token' && e.newValue !== e.oldValue) {
        if (e.newValue) {
          await signIn(JSON.parse(e.newValue))
        } else {
          await signOut()
        }
      }
    }

    window.addEventListener('storage', storageEventHandler, false)

    return () => {
      window.removeEventListener('storage', storageEventHandler, false)
    }
  }, [])

  useEffect(() => {
    if (!isFetching) {
      dispatch(setProfile(!error && profile ? profile : null))
      dispatch(setExistsNotifications(!error && profile ? profile.exists_notifications : false))
    }
  }, [ isFetching, profile, error ])

  return (
    <AuthContext.Provider value={{
      getToken: () => token,
      signIn,
      signOut,
    }}>

      {(() => {

        if (loading) {
          return <Loading position="fixed" />
        }

        if (!error && profile && pathname.startsWith('/login')) {
          const returnTo = searchParams.get('returnTo') || '/'
          try {
            return <Navigate to={returnTo.startsWith('/') ? returnTo : atob(returnTo)} />
          } catch (e) {
            return <Navigate to="/" />
          }
        }

        if (!error || pathname.startsWith('/login')) {
          return children
        }

        switch ((error as ApiError)?.status) {
          case 401:
          case 403:
            const returnTo = btoa(pathname + '?' + searchParams.toString())
            return <Navigate to={`/login?returnTo=${returnTo}`} />
          default:
            return <ErrorPage error={error} />
        }

      })()}
    </AuthContext.Provider>
  )
}

class AuthProviderError extends Error {}

export const useAuthContext = () => {
  const cxt = useContext(AuthContext)

  if (!cxt) {
    throw new AuthProviderError('AuthContext not initialized.')
  }

  return cxt
}

export default AuthProvider